import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { sendGoogleAnalyticsEvents } from '@elfsight/lib-google-analytics';
import { useSettings } from '@wu/widget-core';
import { defaultOpenFormSettings } from '../../constants';
import { FormPopupContext } from './form-popup-context';
import { FormThemeProvider } from './form-theme-provider';
import { getFormFunctionalitySettings } from './utils';

const CLOSE_POPUP_ACTIONS = ['hide', 'redirect'];

export function FormPopupProvider({ children }: PropsWithChildren) {
  const {
    layout,
    actionsDuringCountdown,
    editingItems,
    googleAnalytics,
    widgetId,
    editMode = false
  } = useSettings();
  const isEditingForm = editMode && !!editingItems?.['isEditingForm'];
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const isFormStarted = useRef(false);

  const openPopup = useCallback(() => {
    setIsPopupOpen(true);
  }, []);
  const closePopup = useCallback(() => {
    setIsPopupOpen(false);
  }, []);

  useEffect(() => {
    if (isEditingForm) {
      openPopup();
    }
  }, [isEditingForm, openPopup]);

  useEffect(() => {
    closePopup();
  }, [closePopup, layout]);

  const formSettings = useMemo(() => {
    const formAction = actionsDuringCountdown.find((action) => {
      const { type } = action;

      return type === 'open-form';
    });
    if (!formAction) {
      return { ...defaultOpenFormSettings, fields: [] };
    }

    return getFormFunctionalitySettings(formAction);
  }, [actionsDuringCountdown]);

  const handleFormChange = useCallback(() => {
    if (!isFormStarted.current) {
      isFormStarted.current = true;

      sendGoogleAnalyticsEvents({
        googleAnalyticsEvents: googleAnalytics,
        eventType: 'formStarted',
        valueMapping: {
          widgetId
        },
        editMode
      });
    }
  }, [googleAnalytics, widgetId, editMode]);

  const { submitAction } = formSettings;
  const handleFormSubmit = useCallback(() => {
    if (CLOSE_POPUP_ACTIONS.includes(submitAction)) {
      closePopup();
    }

    sendGoogleAnalyticsEvents({
      googleAnalyticsEvents: googleAnalytics,
      eventType: 'formSubmitted',
      valueMapping: {
        widgetId
      },
      editMode
    });

    isFormStarted.current = false;
  }, [googleAnalytics, submitAction, closePopup, widgetId, editMode]);

  const context = useMemo(
    () => ({
      openPopup,
      closePopup,
      formSettings,
      isPopupOpen,
      onFormChange: handleFormChange,
      onFormSubmit: handleFormSubmit
    }),
    [
      formSettings,
      closePopup,
      isPopupOpen,
      openPopup,
      handleFormChange,
      handleFormSubmit
    ]
  );

  return (
    <FormPopupContext.Provider value={context}>
      <FormThemeProvider>{children}</FormThemeProvider>
    </FormPopupContext.Provider>
  );
}
